import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getToolBySlug } from '../data/tools';
import { getIconComponent } from '../lib/icons';
import { GoogleAd } from '../components/GoogleAd';
import { QrGeneratorTool } from './tools/QrGeneratorTool';
import { ImageEditorTool } from './tools/ImageEditorTool';
import { EcommerceEditorTool } from './tools/EcommerceEditorTool';
import { BadmintonTeamTool } from './tools/BadmintonTeamTool';
import { RegexTesterTool } from './tools/RegexTesterTool';
import { HtmlPreviewTool } from './tools/HtmlPreviewTool';
import { CompoundLoanCalculatorTool } from './tools/CompoundLoanCalculatorTool';
import { ShortUrlTool } from './tools/ShortUrlTool';
import { LineMessagePreviewTool } from './tools/LineMessagePreviewTool';
import { PdfToolsTool } from './tools/PdfToolsTool';
import { WeekendFlightRadarTool } from './tools/WeekendFlightRadarTool';
import { LccFeeCalculatorTool } from './tools/LccFeeCalculatorTool';
import { HotelCouponListTool } from './tools/HotelCouponListTool';
import { FastFoodCouponTool } from './tools/FastFoodCouponTool';
import { AiDrizzleTool } from './tools/AiDrizzleTool';
import { AiSpeechToTextTool } from './tools/AiSpeechToTextTool';
import { AiTextTool } from './tools/AiTextTool';
import { AudioTrimSilenceTool } from './tools/AudioTrimSilenceTool';
import { VideoTrimSilenceTool } from './tools/VideoTrimSilenceTool';
import { HtmlContentEditorTool } from './tools/HtmlContentEditorTool';
import { useCountApiToolStats } from '../hooks/useCountApiToolStats';

/**
 * 工具專屬頁：標題、簡介、依 slug 渲染對應工具內容；找不到 slug 時導回首頁
 */
export function ToolPage() {
  const { slug } = useParams<{ slug: string }>();
  const tool = slug ? getToolBySlug(slug) : undefined;
  const { total, today, loading, error } = useCountApiToolStats({
    toolSlug: tool?.slug ?? null,
  });

  useEffect(() => {
    if (tool) {
      document.title = `${tool.name} | 工具站`;
    }
    return () => {
      document.title = '工具站';
    };
  }, [tool]);

  if (!tool) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-900/20">
        <p className="text-amber-800 dark:text-amber-200">找不到此工具。</p>
        <Link to="/" className="mt-3 inline-block text-primary hover:underline">
          回到首頁
        </Link>
      </div>
    );
  }

  const Icon = getIconComponent(tool.icon);

  /** 依 slug 渲染工具專屬內容（含操作區、結果、廣告位、SEO） */
  const renderToolContent = () => {
    if (tool.slug === 'qr-generator') return <QrGeneratorTool />;
    if (tool.slug === 'image-editor') return <ImageEditorTool />;
    if (tool.slug === 'ecommerce-editor') return <EcommerceEditorTool />;
    if (tool.slug === 'badminton-team') return <BadmintonTeamTool />;
    if (tool.slug === 'regex-tester') return <RegexTesterTool />;
    if (tool.slug === 'html-preview') return <HtmlPreviewTool />;
    if (tool.slug === 'compound-loan-calculator')
      return <CompoundLoanCalculatorTool />;
    if (tool.slug === 'short-url') return <ShortUrlTool />;
    if (tool.slug === 'line-message-preview') return <LineMessagePreviewTool />;
    if (tool.slug === 'pdf-tools') return <PdfToolsTool />;
    if (tool.slug === 'weekend-flight-radar') return <WeekendFlightRadarTool />;
    if (tool.slug === 'lcc-fee-calculator') return <LccFeeCalculatorTool />;
    if (tool.slug === 'hotel-coupon-list') return <HotelCouponListTool />;
    if (tool.slug === 'fast-food-coupon-search') return <FastFoodCouponTool />;
    if (tool.slug === 'ai-drizzle') return <AiDrizzleTool />;
    if (tool.slug === 'ai-speech-to-text') return <AiSpeechToTextTool />;
    if (tool.slug === 'ai-text') return <AiTextTool />;
    if (tool.slug === 'audio-trim-silence') return <AudioTrimSilenceTool />;
    if (tool.slug === 'video-trim-silence') return <VideoTrimSilenceTool />;
    if (tool.slug === 'html-content-editor') return <HtmlContentEditorTool />;
    return <GenericToolPlaceholder />;
  };

  return (
    <div>
      {/* 標題與簡介 */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2">
          {Icon && (
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-6 w-6" />
            </span>
          )}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {tool.name}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">{tool.description}</p>
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {loading && <span>載入使用次數中…</span>}
          {!loading && error && <span>{error}</span>}
          {!loading && !error && (
            <span>
              今日使用次數：{today ?? 0}，累計使用次數：{total ?? 0}
            </span>
          )}
        </div>
      </div>

      {renderToolContent()}

      {/* 工具頁底部的廣告位 */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <GoogleAd format="auto" style={{ minHeight: '100px' }} />
      </div>
    </div>
  );
}

/**
 * 尚未實作之工具的佔位內容（通用輸入/結果/SEO）
 */
function GenericToolPlaceholder() {
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const handleRun = () => {
    setResult(inputValue.trim() || '(未輸入)');
  };

  const seoContent =
    '此為工具說明區塊，可放置 300–500 字的使用教學與常見問題，利於 SEO。之後可替換為真實內容。';

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <label
          htmlFor="tool-input"
          className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          輸入
        </label>
        <div className="flex flex-wrap gap-2">
          <input
            id="tool-input"
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder="請輸入..."
            className="min-w-[200px] flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          />
          <button
            type="button"
            onClick={handleRun}
            className="rounded bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90"
          >
            執行
          </button>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          結果
        </h2>
        <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">
          {result ?? '—'}
        </p>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          {seoContent}
        </p>
      </section>
    </>
  );
}
